/*
 * -----------------------------------------------------------------------
 *  <copyright file="studies.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql, TypedDocumentNode } from '@apollo/client';
import {
    ManageStudyFragment,
    StudyFragment,
    StudyMemberFragment,
    StudyRestrictedFragment,
    StudyPluginFragment,
    StudyOverviewTableFragment, NavbarStudyFragment, SmStudyMemberFragment
} from './fragments';
import {
    getSmStudyMembers,
    getSmStudyMembersVariables,
    getStudy,
    getStudyVariables,
    getTrialOSStudyTeamMembers,
    getTrialOSStudyTeamMembersVariables,
    getTrialOSStudyTeamMembersForUser,
    getTrialOSStudyTeamMembersForUserVariables,
    setTrialOSStudyTeamMembership,
    setTrialOSStudyTeamMembershipVariables,
} from '@queries/generated/studies.generated';

export const SAVE_STUDY = gql`
    mutation saveStudy($study: StudyInput!, $reason: String) {
        saveStudy(study: $study, reason: $reason) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const ASSIGN_FUNCTIONS = gql`
    mutation assignFunctions($studyId: ObjectId!, $reason: String, $studyFunctionIds: [ObjectId]!) {
        assignFunctions(studyId: $studyId, reason: $reason, studyFunctionIds: $studyFunctionIds)
    }
`;

export const ASSIGN_PRODUCTS = gql`
    mutation assignProducts($studyId: ObjectId!, $reason: String, $studyProductIds: [ObjectId]!) {
        assignProducts(studyId: $studyId, reason: $reason, studyProductIds: $studyProductIds)
    }`;

export const ASSIGN_INDICATIONS = gql`
    mutation assignIndications($studyId: ObjectId!, $reason: String, $studyIndicationIds: [ObjectId]!) {
        assignIndications(studyId: $studyId, reason: $reason, studyIndicationIds: $studyIndicationIds)
    }`;

export const UPDATE_STUDY_MODULES = gql`
    mutation updateStudyModules($studyId: ObjectId!, $modules: ConvertableInt!, $reason: String) {
        updateStudyModules(studyId: $studyId, modules: $modules, reason: $reason) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const GET_STUDIES = gql`
    query getStudies($timestamp: DateTime) {
        organisationStudies(timestamp: $timestamp) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const GET_STUDY = gql`
    query getStudy($id: ObjectId!, $timestamp: DateTime) {
        study(id: $id, timestamp: $timestamp) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const GET_STUDY_CONFIG: TypedDocumentNode<getStudy, getStudyVariables> = gql`
    query getLiveStudyConfig($id: ObjectId!, $timestamp: DateTime) {
        study(id: $id, timestamp: $timestamp) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const GET_STUDY_USING_CM2 = gql`
    query studyUsingCM2($studyId: ObjectId!) {
        studyUsingCM2(studyId: $studyId)
    }
`;

export const GET_RESTRICTED_STUDY = gql`
    query getRestrictedStudy($id: ObjectId!, $timestamp: DateTime) {
        study(id: $id, timestamp: $timestamp) {
            ...StudyRestrictedFields
        }
    }
    ${StudyRestrictedFragment}
`;

export const GET_RISK_LIBRARY_CATEGORIES = gql`
    query getRiskLibraryCategories($id: ObjectId!, $timestamp: DateTime) {
        study(id: $id, timestamp: $timestamp) {
            id
            riskLibraryCategoryIds
        }
    }
`;

export const GET_STUDY_PLUGIN = gql`
    query getStudyPlugin($id: ObjectId!, $timestamp: DateTime) {
        study(id: $id, timestamp: $timestamp) {
            ...StudyPluginFields
        }
    }
    ${StudyPluginFragment}
`;

export const GET_STUDY_LOGO = gql`
    query getStudyLogo($studyId: ObjectId!, $timestamp: DateTime) {
        study(id: $studyId, timestamp: $timestamp) {
            id
            logo
        }
    }
`;

export const SET_STUDY_LOGO = gql`
    mutation setStudyLogo($studyId: ObjectId!, $logo: String!, $reason: String) {
        setStudyLogo(studyId: $studyId, logo: $logo, reason: $reason) {
            id
            logo
        }
    }
`;

export const GET_STUDY_MEMBERS = gql`
    query getStudyMembers($studyId: ObjectId!, $timestamp: DateTime) {
        studyMembers(studyId: $studyId, timestamp: $timestamp) {
            ...StudyMemberFields
        }
    }
    ${StudyMemberFragment}
`;

export const GET_SM_STUDY_MEMBERS: TypedDocumentNode<getSmStudyMembers, getSmStudyMembersVariables> = gql`
    query getSmStudyMembers($studyId: ObjectId!, $timestamp: DateTime) {
        smStudyMembers(studyId: $studyId, timestamp: $timestamp) {
            ...SmStudyMemberFields
        }
    },
    ${SmStudyMemberFragment}
`;

export const GET_TRIALOS_STUDY_TEAM_MEMBERS: TypedDocumentNode<
    getTrialOSStudyTeamMembers,
    getTrialOSStudyTeamMembersVariables
> = gql`
    query getTrialOSStudyTeamMembers($studyId: ObjectId!, $timestamp: DateTime) {
        trialOSStudyTeamMembers(studyId: $studyId, timestamp: $timestamp) {
            id
            userId
            firstName
            lastName
            email
            riskIQ
            created
            createdBy {
                firstName
                lastName
            }
            updated
            updatedBy {
                firstName
                lastName
            }
        }
    }
`;

export const GET_TRIALOS_STUDY_TEAM_MEMBERS_FOR_USER: TypedDocumentNode<
    getTrialOSStudyTeamMembersForUser,
    getTrialOSStudyTeamMembersForUserVariables
> = gql`
    query getTrialOSStudyTeamMembersForUser($userId: ObjectId!) {
        trialOSStudyTeamMembersForUser(userId: $userId) {
            id
            studyId
            userId
            riskIQ
        }
    }
`;

export const SET_TRIALOS_STUDY_TEAM_MEMBERSHIP: TypedDocumentNode<
    setTrialOSStudyTeamMembership,
    setTrialOSStudyTeamMembershipVariables
> = gql`
    mutation setTrialOSStudyTeamMembership(
        $userId: ObjectId!
        $studies: [TrialOSStudyAccessInput!]!
        $reason: String
    ) {
        setTrialOSStudyTeamMembership(userId: $userId, studies: $studies, reason: $reason) {
            id
            studyId
            userId
            riskIQ
        }
    }
`;

export const SAVE_STUDY_MEMBER = gql`
    mutation saveStudyMember($studyId: ObjectId!, $studyMember: StudyMemberInput!, $reason: String) {
        saveStudyMember(studyId: $studyId, studyMember: $studyMember, reason: $reason) {
            ...StudyMemberFields
        }
    }
    ${StudyMemberFragment}
`;

export const SAVE_SM_STUDY_MEMBER = gql`
    mutation saveSmStudyMember($studyId: ObjectId!, $smStudyMember: SmStudyMemberInput!, $reason: String) {
        saveSmStudyMember(studyId: $studyId, smStudyMember: $smStudyMember, reason: $reason) {
            ...SmStudyMemberFields
        }
    }
    ${SmStudyMemberFragment}
`;


export const SET_USER_STUDY_MEMBERSHIP = gql`
    mutation setUserStudyMembership($reason: String!, $userId: ObjectId!, $studyIds: [ObjectId]!, $isAdmins: [Boolean]!) {
        setUserStudyMembership(reason: $reason, userId: $userId, studyIds: $studyIds, isAdmins: $isAdmins) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const SET_SM_STUDY_MEMBERSHIP = gql`
    mutation setSmStudyMembership($reason: String!, $userId: ObjectId!, $studyIds: [ObjectId]!, $isAdmins: [Boolean]!) {
        setSmStudyMembership(reason: $reason, userId: $userId, studyIds: $studyIds, isAdmins: $isAdmins) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const GET_USER_STUDIES = gql`
    query userStudies($userId: ObjectId!, $organisationKey: ObjectId) {
        userStudies(userId: $userId, organisationKey: $organisationKey) {
            ...StudyFields
        }
    }
    ${StudyFragment}
`;

export const GET_USER_STUDIES_NAVBAR = gql`
    query userStudiesNavbar($userId: ObjectId!, $organisationKey: ObjectId) {
        userStudies(userId: $userId, organisationKey: $organisationKey) {
            ...NavbarStudyFields
        }
    }
    ${NavbarStudyFragment}
`;

export const GET_MANAGE_ORG_STUDIES = gql`
    query organisationStudies {
        organisationStudies {
            ...ManageStudyFields
        }
    }
    ${ManageStudyFragment}
`;

export const SET_STUDY_FAVOURITE = gql`
    mutation setStudyFavourite($studyId: ObjectId!, $favourite: Boolean!, $reason: String) {
        setStudyFavourite(studyId: $studyId, favourite: $favourite, reason: $reason)
    }    
`;

export const SET_SM_STUDY_FAVOURITE = gql`
    mutation setSmStudyFavourite($studyId: ObjectId!, $favourite: Boolean!, $reason: String) {
        setSmStudyFavourite(studyId: $studyId, favourite: $favourite, reason: $reason)
    }    
`;

export const SET_STUDY_DEFAULT = gql`
    mutation setStudyDefault($studyId: ObjectId!, $reason: String) {
        setStudyDefault(studyId: $studyId, reason: $reason)
    }
`;

export const SET_STUDY_SITE_FAVOURITE = gql`
    mutation setStudySiteFavourite($studyId: ObjectId!, $siteId: ObjectId!, $favourite: Boolean!, $reason: String) {
        setStudySiteFavourite(studyId: $studyId, siteId: $siteId, favourite: $favourite, reason: $reason)
    }
`;

export const SET_SM_STUDY_SITE_FAVOURITE = gql`
    mutation setSmStudySiteFavourite($studyId: ObjectId!, $siteId: ObjectId!, $favourite: Boolean!, $reason: String) {
        setSmStudySiteFavourite(studyId: $studyId, siteId: $siteId, favourite: $favourite, reason: $reason)
    }
`;

export const SET_STUDY_SITE_DEFAULT = gql`
    mutation setStudySiteDefault($studyId: ObjectId!, $siteId: ObjectId!, $reason: String) {
        setStudySiteDefault(studyId: $studyId, siteId: $siteId, reason: $reason)
    }
`;

export const SET_SM_STUDY_SITE_DEFAULT = gql`
    mutation setSmStudySiteDefault($studyId: ObjectId!, $siteId: ObjectId!, $reason: String) {
        setSmStudySiteDefault(studyId: $studyId, siteId: $siteId, reason: $reason)
    }
`;

export const SAVE_STUDY_CONTRACTUAL_INFORMATION = gql`
    mutation saveStudyContractualInformation(
        $studyId: ObjectId!,
        $riskAssessmentRfpStartDate: Date,
        $riskAssessmentIsmpStartDate: Date,
        $riskAssessmentAwardStartDate: Date,
        $centralMonitoringFirstDataLoadDate: Date,
        $centralMonitoringReleaseDate: Date
    ) {
        saveStudyContractualInformation(
            studyId: $studyId,
            riskAssessmentRfpStartDate: $riskAssessmentRfpStartDate,
            riskAssessmentIsmpStartDate: $riskAssessmentIsmpStartDate,
            riskAssessmentAwardStartDate: $riskAssessmentAwardStartDate,
            centralMonitoringFirstDataLoadDate: $centralMonitoringFirstDataLoadDate,
            centralMonitoringReleaseDate: $centralMonitoringReleaseDate
        )
    }
`;

export const SAVE_RISK_ASSESSMENT_CONFIGURATION = gql`
    mutation saveRiskAssessmentConfiguration(
        $studyId: ObjectId!,
        $riskLibraryCategoryIds: [ObjectId],
        $evaluationModelId: ObjectId,
        $riskReviewWindowDays: ConvertableInt,
        $numberTotal: Boolean,
        $numberActive: Boolean,
        $numberActiveCritical: Boolean,
        $numberActiveCtQsWithoutRisks: Boolean,
        $numberActiveCtQsWithoutFunctionalDataReviews: Boolean
    ) {
        saveRiskAssessmentConfiguration(
            studyId: $studyId,
            riskLibraryCategoryIds: $riskLibraryCategoryIds,
            evaluationModelId: $evaluationModelId,
            riskReviewWindowDays: $riskReviewWindowDays,
            numberTotal: $numberTotal,
            numberActive: $numberActive,
            numberActiveCritical: $numberActiveCritical,
            numberActiveCtQsWithoutRisks: $numberActiveCtQsWithoutRisks,
            numberActiveCtQsWithoutFunctionalDataReviews: $numberActiveCtQsWithoutFunctionalDataReviews
        )
    }
`;

export const SAVE_RISK_OVERVIEW_CONFIGURATION = gql`
    mutation saveRiskOverviewConfiguration(
        $studyId: ObjectId!,
        $subjectField: String!,
        $amberKris: Boolean!,
        $redKris: Boolean!,
        $amberDecisionKris: Boolean!,
        $redDecisionKris: Boolean!,
        $siteStatus: Boolean!,
        $siteActiveDate: Boolean!,
        $siteCountry: Boolean!,
        $siteMonitor: Boolean!,
        $numSubjects: Boolean!,
        $displayRiskOverviewOnStudySummary: Boolean!
    ) {
        saveRiskOverviewConfiguration(
            studyId: $studyId,
            subjectField: $subjectField,
            amberKris: $amberKris,
            redKris: $redKris,
            amberDecisionKris: $amberDecisionKris,
            redDecisionKris: $redDecisionKris,
            siteStatus: $siteStatus,
            siteActiveDate: $siteActiveDate,
            siteCountry: $siteCountry,
            siteMonitor: $siteMonitor,
            numSubjects: $numSubjects,
            displayRiskOverviewOnStudySummary: $displayRiskOverviewOnStudySummary
        )
    }  
`;

export const SAVE_SITE_SUMMARY_CONFIGURATION = gql`
    mutation saveSiteSummaryConfiguration(
        $studyId: ObjectId!,
        $siteActiveDate: Boolean!,
        $siteCountry: Boolean!,
        $siteStatus: Boolean!,
        $monitor: Boolean!,
        $numScreened: Boolean!,
        $numScreenFailed: Boolean!,
        $numEnrolled: Boolean!,
        $numRandomised: Boolean!,
        $numTreatmentDiscontinued: Boolean!,
        $numStudyDiscontinued: Boolean!,
        $numCompleted: Boolean!,
        $observationChart: Boolean!,
        $actionChart: Boolean!
    ) {
        saveSiteSummaryConfiguration(
            studyId: $studyId,
            siteActiveDate: $siteActiveDate,
            siteCountry: $siteCountry,
            siteStatus: $siteStatus,
            monitor: $monitor,
            numScreened: $numScreened,
            numScreenFailed: $numScreenFailed,
            numEnrolled: $numEnrolled,
            numRandomised: $numRandomised,
            numTreatmentDiscontinued: $numTreatmentDiscontinued,
            numStudyDiscontinued: $numStudyDiscontinued,
            numCompleted: $numCompleted,
            observationChart: $observationChart,
            actionChart: $actionChart
        )
    }
`;

export const SAVE_STUDY_SUMMARY_CONFIGURATION = gql`
    mutation saveStudySummaryConfiguration(
        $studyId: ObjectId!,
        $numScreened: Boolean!,
        $numScreenFailed: Boolean!,
        $numEnrolled: Boolean!,
        $numRandomised: Boolean!,
        $numTreatmentDiscontinued: Boolean!,
        $numStudyDiscontinued: Boolean!,
        $numCompleted: Boolean!        
    ) {
        saveStudySummaryConfiguration(
            studyId: $studyId,
            numScreened: $numScreened,
            numScreenFailed: $numScreenFailed,
            numEnrolled: $numEnrolled,
            numRandomised: $numRandomised,
            numTreatmentDiscontinued: $numTreatmentDiscontinued,
            numStudyDiscontinued: $numStudyDiscontinued,
            numCompleted: $numCompleted            
        )
    }
`;

export const SAVE_STUDY_CONFIGURATION = gql`
    mutation saveStudyConfiguration(
        $studyId: ObjectId!,
        $studyNumber: String!,
        $shortName: String!,
        $title: String!,
        $sponsor: String!,
        $program: ObjectId,
        $configId: ObjectId!,
        $useLatestCentralMonitoring: Boolean!
    ) {
        saveStudyConfiguration(
            studyId: $studyId,
            studyNumber: $studyNumber,
            shortName: $shortName,
            title: $title,
            sponsor: $sponsor,
            program: $program,
            configId: $configId,
            useLatestCentralMonitoring: $useLatestCentralMonitoring
        )
    }
`;

export const SAVE_CENTRAL_MONITORING_CONFIGURATION = gql`
    mutation saveCentralMonitoringConfiguration(
        $studyId: ObjectId!,
        $importDefinitionsKey: String,
        $volumeAdjustedScoring: Boolean!,
        $siteStatusesToExclude: [String]!,
        $scoreGreenThreshold: ConvertableFloat!,
        $scoreAmberThreshold: ConvertableFloat!,
        $scoreThreshold: ConvertableFloat!,
        $volumeAdjustedScoringNumberField: String,
        $adjustmentWeight: ConvertableFloat!,
        $svtDependency: SvtDependencyType,
        $calculateVisits: Boolean!,
        $calculateAssessments: Boolean!,
        $calculateCrfs: Boolean!,
        $lastSubjectScreenedDate: Date,
        $lastSubjectEnrolledDate: Date,
        $lastSubjectRandomisedDate: Date,
        $useInformedConsentedDate: Boolean!,
        $assessmentAssociation: Boolean!
        $automationActive: Boolean!,
        $studyAutomationColor: StudyAutomationColor!,
        $controlsAutomationActive: Boolean!,
        $controlsAutomationColor: ControlsAutomationColor!,
        $integrationActive: Boolean!,
        $integrationStudyNumber: String!,
    ) {
        saveCentralMonitoringConfiguration(
            studyId: $studyId,
            importDefinitionsKey: $importDefinitionsKey,
            volumeAdjustedScoring: $volumeAdjustedScoring,
            siteStatusesToExclude: $siteStatusesToExclude,
            scoreGreenThreshold: $scoreGreenThreshold,
            scoreAmberThreshold: $scoreAmberThreshold,
            scoreThreshold: $scoreThreshold,
            volumeAdjustedScoringNumberField: $volumeAdjustedScoringNumberField,
            adjustmentWeight: $adjustmentWeight,
            svtDependency: $svtDependency,
            calculateVisits: $calculateVisits,
            calculateAssessments: $calculateAssessments,
            calculateCrfs: $calculateCrfs,
            lastSubjectScreenedDate: $lastSubjectScreenedDate,
            lastSubjectEnrolledDate: $lastSubjectEnrolledDate,
            lastSubjectRandomisedDate: $lastSubjectRandomisedDate,
            useInformedConsentedDate: $useInformedConsentedDate,
            assessmentAssociation: $assessmentAssociation,
            automationActive: $automationActive,
            studyAutomationColor: $studyAutomationColor
            controlsAutomationActive: $controlsAutomationActive,
            controlsAutomationColor: $controlsAutomationColor,
            integrationActive: $integrationActive,
            integrationStudyNumber: $integrationStudyNumber,
        )
    }
`;

export const SAVE_STUDY_OVERVIEW_TABLE_CONFIGURATION = gql`
    mutation saveStudyOverviewTableConfiguration(
        $studyId: ObjectId!,
        $studyOverviewTableConfigurationInputType: StudyOverviewTableConfigurationInputType!,
        $displayStudyOverviewOnStudySummary: Boolean!,
        $reason : String
    ) {
        saveStudyOverviewTableConfiguration(
            studyId: $studyId,
            studyOverviewTableConfiguration: $studyOverviewTableConfigurationInputType,
            displayStudyOverviewOnStudySummary: $displayStudyOverviewOnStudySummary,
            reason: $reason
        )
    }
`;

export const SAVE_STUDY_SITE_OVERVIEW_TABLE_CONFIGURATION = gql`
    mutation saveStudySiteOverviewTableConfiguration(
        $studyId: ObjectId!,
        $studySiteOverviewTableConfigurationInputType: StudySiteOverviewTableConfigurationInputType!,
        $displayStudySiteOverviewOnStudySummary: Boolean!,
        $reason : String
    ) {
        saveStudySiteOverviewTableConfiguration(
            studyId: $studyId,
            studySiteOverviewTableConfiguration: $studySiteOverviewTableConfigurationInputType,
            displayStudySiteOverviewOnStudySummary: $displayStudySiteOverviewOnStudySummary,
            reason: $reason
        )
    }
`;

export const GET_STUDY_SUMMARIES = gql`
    query getStudySummaries {
        studySummaries {
            studyId
            studyNum
            sponsor
            screenedSubjects
            enrolledSubjects
            studyDiscontinuedSubjects
            treatmentDiscontinuedSubjects
            completedSubjects
            screenFailedSubjects
            randomisedSubjects
            numRedRisks
            numAmberRisks
            numGreenRisks
            numMyRisks
            numMyOverdueRisks
            numMyReviewedRisks
            numMyReviewDueRisks
            numUnevaluatedRisks
            numMyRiskActions
            numMyOpenRiskActions
            numMyNotReviewedRisks
            numMyOpenRiskActions
            numMyClosedRiskActions
            numMyRiskObservations
            numMyOpenRiskObservations
            numMyClosedRiskObservations
            numMyCentralMonitoringActions
            numMyOpenCentralMonitoringActions
            numMyClosedCentralMonitoringActions
            numMyCentralMonitoringObservations
            numMyOpenCentralMonitoringObservations
            numMyClosedCentralMonitoringObservations
            siteScores {
                score
                siteId
                color
            }
        }
    }  
`;

export const GET_STUDY_CONFIG_EXPORT = gql`
    query studyConfigExport($studyId: ObjectId!) {
        studyConfigExport(studyId: $studyId)
    }
`;

export const IMPORT_STUDY_CONFIGURATION = gql`
    mutation importStudyConfiguration($contents: String!, $studyId: ObjectId!, $studyName: String, $importTypes: [StudyConfigurationImportType]!) {
        importStudyConfiguration(contents: $contents, studyName: $studyName, studyId: $studyId, importTypes: $importTypes) {
            study
            studyAssessments
            studyCohorts
            studyTreatmentArms
            studyCrfs
            panels
            dataTables
            dashboards
            extraDataFields
            svts
            svtVisits
            errors
            warnings
        }
    }
`;

export const STUDY_CONFIG_IMPORT = gql`
    mutation studyConfigImport($contents: String!, $studyId: ObjectId, $observationCount: Int, $targetConfigHash: String) {
        studyConfigImport(contents: $contents, observationCount: $observationCount, studyId: $studyId, targetConfigHash: $targetConfigHash) {
            exportedBy
            exportedFrom
            exportedUtcTimestamp
            fileCreationMethod
            fileFormatVersion
            isSourceEnvironment
            itemResults {
                action
                message
                name
                sourceConfigId
                sourceId
                status
                targetConfigId
                targetId
                type
            }
            observationCount
            organisation
            success
            targetConfigHash
        }
    }
`;

export const GET_STUDY_SUMMARY_OVERVIEW_TABLE = gql`
    query studyOverviewTable($currentDataSetId: ObjectId!, $previousDataSetId: ObjectId!, $studyId: ObjectId!) {
        studyOverviewTable(currentDataSetId: $currentDataSetId, previousDataSetId: $previousDataSetId, studyId: $studyId){
            ...StudyOverviewTableFields
        }
    }
    ${StudyOverviewTableFragment}
`;
